import * as React from 'react';
import { Form, DatePickerRange, CustomSubmitButton, Select, TextInput } from "soprasteria-ui-library";
import { IListItem } from "soprasteria-ui-library/dist/models/IFormInput";
import { IImportedXmlSearchParams } from '../../../models/IImportedXml';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { LIBRETTOADMIN, VÅRDANSVARIG } from '../../../utils/RoleConstants';
import { CareProviderDropdown } from '../../CareProviderDropdown/CareProviderDropdown';
import { ICareProvider } from '../../../models/ICareProvider';
import { useAuth } from '../../../App';
import { ImportXmlApi } from '../../../api/ImportXmlApi';


interface IImportedXmlSearchForm {
    importedXmlTypes: IListItem[];
    searchImportedXmls: (searchParams: IImportedXmlSearchParams) => void;
    isFetchingImportedXmls: boolean;
    preSetSearchParams?: IImportedXmlSearchParams;
}

export const ImportedXmlSearchForm: React.FunctionComponent<IImportedXmlSearchForm> = ({ importedXmlTypes, searchImportedXmls, isFetchingImportedXmls, preSetSearchParams }) => {

    const auth = useAuth();
    const api = new ImportXmlApi(auth);
    const abortController = new AbortController();

    const userInfo = React.useContext(UserContext);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);
    const hasRoleSystemadministrator = isInRole(LIBRETTOADMIN, userInfo.roles);

    const [isFetchingCareProviders, setIsFetchingCareProviders] = React.useState<boolean>(false);
    const [isFetchingUmbrellaOrganisations, setIsFetchingUmbrellaOrganisations] = React.useState<boolean>(false);

    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>([]);
    const [umbrellaOrganisations, setUmbrellaOrganisations] = React.useState<ICareProvider[]>([]);

    const [selectedUmbrellaOrganisationId, setSelectedUmbrellaOrganisationId] = React.useState<number>();
    const [resetCareProviderValue, doResetCareProviderValue] = React.useState<boolean>(false);

    React.useEffect(() => {
        getUmbrellaOrganisations();

        return () => {
            abortController.abort();
        }
    }, [])

    const getUmbrellaOrganisations = () => {
        setIsFetchingUmbrellaOrganisations(true);
        api.getUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaCareProviderIdToUse;
            if (preSetSearchParams?.umbrellaCareProviderId) {
                umbrellaCareProviderIdToUse = parseInt(preSetSearchParams.umbrellaCareProviderId.toString());
                setSelectedUmbrellaOrganisationId(umbrellaCareProviderIdToUse)
                getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
            }
            else if (!hasRoleSystemadministrator) {
                umbrellaCareProviderIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedUmbrellaOrganisationId(umbrellaOrganisations[0].careProviderId);
                getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderIdToUse, umbrellaOrganisations);
            }

            setUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingUmbrellaOrganisations(false);
        });
    }

    const getCareProvidersByUmbrellaCareProviderId = (umbrellaCareProviderId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingCareProviders(true);
        api.getCareProvidersByUmbrellaCareProviderId(umbrellaCareProviderId, (careProviders) => {
            if (careProviders.length !== 0) {
                setCareProviders(careProviders);
            } else {
                umbrellaCareProviderId = parseInt(umbrellaCareProviderId.toString());
                let umbrellaCareProvider = umbrellaOrganisationsAlt !== undefined
                    ? umbrellaOrganisationsAlt.find(c => c.careProviderId === umbrellaCareProviderId)
                    : umbrellaOrganisations.find(c => c.careProviderId === umbrellaCareProviderId);
                setCareProviders([umbrellaCareProvider!]);
            }
            setIsFetchingCareProviders(false);
        }, undefined, () => { });
    }

    const onUmbrellaCareProviderChange = (selectedCareProvider: string | undefined) => {
        if (hasRoleSystemadministrator) {
            if (selectedCareProvider && selectedCareProvider !== "") {
                getCareProvidersByUmbrellaCareProviderId(parseInt(selectedCareProvider));
                setSelectedUmbrellaOrganisationId(parseInt(selectedCareProvider));
            }
            else {
                setSelectedUmbrellaOrganisationId(undefined);
                setCareProviders([]);
            }
            doResetCareProviderValue(prev => !prev);
        }
    }

    const getUmbrellaCareProviderDefaultValue = () => {
        if (umbrellaOrganisations && preSetSearchParams?.umbrellaCareProviderId) {
            return preSetSearchParams.umbrellaCareProviderId.toString();
        }

        if (selectedUmbrellaOrganisationId) {
            return selectedUmbrellaOrganisationId.toString();
        }

        if (umbrellaOrganisations?.length === 1) {
            return umbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }

    const getCareProviderDefaultValue = () => {
        if (careProviders) {
            const preSetCareProviderIdCasted = preSetSearchParams?.careProviderId
                ? parseInt(preSetSearchParams.careProviderId.toString())
                : undefined;

            const preSetCareProviderIdExistInCareProviders = preSetCareProviderIdCasted !== undefined
                ? careProviders.some((careProvider) => careProvider.careProviderId === preSetCareProviderIdCasted)
                : undefined;

            if (careProviders && preSetCareProviderIdExistInCareProviders) {
                return preSetSearchParams?.careProviderId?.toString();
            }

            if (careProviders && careProviders.length === 1 && careProviders[0] !== undefined) {
                return careProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    return (
        <Form>
            <div className="row">
                <div className="col-6">
                    <DatePickerRange label="Datum" name="dateFrom" nameSecondary="dateTo" inlineLabel
                        value={preSetSearchParams?.dateFrom ? new Date(preSetSearchParams.dateFrom) : undefined}
                        valueSecondary={preSetSearchParams?.dateTo ? new Date(preSetSearchParams.dateTo) : undefined} />
                </div>
                <div className="col-6">
                    <Select
                        label="Filtyp"
                        name="importedXmlTypeId"
                        options={importedXmlTypes}
                        placeholder="Välj en filtyp"
                        inlineLabel
                        required={false}
                        isClearable
                        selectedValue={preSetSearchParams?.importedXmlTypeId?.toString()}
                    />
                </div>
                <div className="col-6">
                    <CareProviderDropdown
                        name="umbrellaCareProviderId"
                        label="Vårdgivare"
                        isLoading={isFetchingUmbrellaOrganisations}
                        careProviders={umbrellaOrganisations}
                        onValueChange={onUmbrellaCareProviderChange}
                        defaultValue={getUmbrellaCareProviderDefaultValue()}
                        disabled={!hasRoleSystemadministrator}
                        useFixedListItemHeight
                        showOrgNum
                        placeholder="Välj vårdgivare"
                        isClearable
                    />
                </div>
                <div className="col-6">
                    <TextInput name="externalId" label="Externt ID" inlineLabel defaultValue={preSetSearchParams?.externalId} />
                </div>
                <div className="col-6">
                    <CareProviderDropdown
                        name="careProviderId"
                        label="Mottagning"
                        isLoading={isFetchingCareProviders}
                        careProviders={careProviders ?? []}
                        defaultValue={getCareProviderDefaultValue()}
                        disabled={!(hasRoleParaply || hasRoleSystemadministrator)}
                        useFixedListItemHeight
                        showClinicId
                        isClearable
                        resetValue={resetCareProviderValue}
                    />
                </div>
                <div className="col-6">
                    <TextInput name="personalSecurityId" label="Personnummer" inlineLabel defaultValue={preSetSearchParams?.personalSecurityId} />
                </div>
                <div className="col-12">
                    <div className="text-right panel">
                         <span className="d-none">
                            <CustomSubmitButton onButtonSubmit={searchImportedXmls} isLoading={isFetchingImportedXmls}>Sök</CustomSubmitButton>
                        </span>
                        <CustomSubmitButton id="submitbutton" onButtonSubmit={searchImportedXmls} isLoading={isFetchingImportedXmls}>Sök</CustomSubmitButton>
                    </div>
                </div>
            </div>
        </Form>
    );
}